import type {
  AppState,
  CandidateLens,
  LensConfiguration,
  MonteCarloResult,
  SimulationResult,
} from './types'

export type CandidateRunSubject =
  | { kind: 'simulation'; candidateId: string; inputFingerprint: string }
  | { kind: 'tolerance'; candidateId: string; inputFingerprint: string }

const pitchFromCandidate = (candidate: CandidateLens): number | null =>
  candidate.pitchMm ??
  (typeof candidate.lpi === 'number' && Number.isFinite(candidate.lpi) && candidate.lpi > 0
    ? 25.4 / candidate.lpi
    : null)

export function lensConfigurationForCandidate(
  state: AppState,
  candidate: CandidateLens,
): LensConfiguration {
  const pitchMm = pitchFromCandidate(candidate)
  const lpi = pitchMm ? 25.4 / pitchMm : null

  if (candidate.mode === 'thin') {
    return {
      ...state.lens,
      mode: 'thin',
      thin: {
        ...state.lens.thin,
        useDesignTarget: false,
        pitchMm,
        lpi,
        focalLengthMm: candidate.focalLengthMm,
        focalReference: candidate.focalReference,
        fillFactor: candidate.fillFactor,
        lateralOffsetMm: candidate.lateralOffsetMm,
        pitchToleranceMm: candidate.pitchToleranceMm,
        focalToleranceMm: candidate.focalToleranceMm,
      },
    }
  }

  return {
    ...state.lens,
    mode: 'thick',
    thick: {
      ...state.lens.thick,
      pitchMm,
      radiusMm: candidate.radiusMm,
      refractiveIndex: candidate.refractiveIndex,
      centerThicknessMm: candidate.centerThicknessMm,
      fillFactor: candidate.fillFactor,
      lateralOffsetMm: candidate.lateralOffsetMm,
      pitchToleranceMm: candidate.pitchToleranceMm,
      radiusToleranceMm: candidate.radiusToleranceMm,
      thicknessToleranceMm: candidate.thicknessToleranceMm,
    },
  }
}

export function simulationInputFingerprint(
  state: AppState,
  lens: LensConfiguration = state.lens,
): string {
  return JSON.stringify({
    lcd: state.lcd,
    layers: state.layers,
    view: state.view,
    lens,
    settings: state.simulationSettings,
  })
}

export function toleranceInputFingerprint(
  state: AppState,
  simulationFingerprint = simulationInputFingerprint(state),
): string {
  return JSON.stringify({
    simulationFingerprint,
    settings: state.toleranceSettings,
  })
}

export function candidateSimulationInputFingerprint(
  candidate: CandidateLens,
  state: AppState,
): string {
  return simulationInputFingerprint(state, lensConfigurationForCandidate(state, candidate))
}

export function candidateToleranceInputFingerprint(
  candidate: CandidateLens,
  state: AppState,
): string {
  const simulationFingerprint = candidateSimulationInputFingerprint(candidate, state)
  return toleranceInputFingerprint(state, simulationFingerprint)
}

function currentCandidateForSubject(
  state: AppState,
  subject: CandidateRunSubject,
  resultFingerprint: string | null,
  expectedFingerprint: (candidate: CandidateLens) => string,
): CandidateLens | null {
  if (!resultFingerprint || subject.inputFingerprint !== resultFingerprint) return null
  const matches = state.candidates.filter((candidate) => candidate.id === subject.candidateId)
  if (matches.length !== 1) return null
  const candidate = matches[0]
  return expectedFingerprint(candidate) === subject.inputFingerprint ? candidate : null
}

export function adoptSimulationEvidence(
  state: AppState,
  subject: Extract<CandidateRunSubject, { kind: 'simulation' }>,
  result: SimulationResult,
  resultFingerprint: string | null,
  runAt: string,
): AppState {
  if (subject.kind !== 'simulation') return state
  if (!Number.isFinite(Date.parse(runAt))) return state
  const candidate = currentCandidateForSubject(
    state,
    subject,
    resultFingerprint,
    (current) => candidateSimulationInputFingerprint(current, state),
  )
  if (!candidate) return state
  const expectedModel = candidate.mode === 'thin'
    ? 'paraxial thin-lens simulation'
    : 'geometric thick-lens ray trace'
  if (result.model !== expectedModel) return state

  const crosstalkMax = Math.max(...result.crosstalk)
  const throughputMin = Math.min(...result.desiredTransmission)
  const zoneCenterErrorMaxMm = Math.max(...result.zoneCenterErrorsMm.map(Math.abs))
  if (
    ![crosstalkMax, throughputMin, zoneCenterErrorMaxMm].every(Number.isFinite) ||
    crosstalkMax < 0 || crosstalkMax > 1 ||
    throughputMin < 0 || throughputMin > 1 ||
    zoneCenterErrorMaxMm < 0
  ) return state

  return {
    ...state,
    candidates: state.candidates.map((current) => current.id === subject.candidateId
      ? {
          ...current,
          simulated: {
            model: result.model,
            candidateId: subject.candidateId,
            inputFingerprint: subject.inputFingerprint,
            crosstalkMax,
            throughputMin,
            zoneCenterErrorMaxMm,
            runAt,
          },
        }
      : current),
  }
}

export function adoptToleranceEvidence(
  state: AppState,
  subject: Extract<CandidateRunSubject, { kind: 'tolerance' }>,
  result: MonteCarloResult,
  resultFingerprint: string | null,
  runAt: string,
): AppState {
  if (subject.kind !== 'tolerance') return state
  if (!Number.isFinite(Date.parse(runAt))) return state
  const candidate = currentCandidateForSubject(
    state,
    subject,
    resultFingerprint,
    (current) => candidateToleranceInputFingerprint(current, state),
  )
  if (!candidate) return state
  const expectedModel = candidate.mode === 'thin' ? 'analytical/paraxial' : 'geometric ray'
  if (result.evaluationModel !== expectedModel) return state
  if (
    !Number.isFinite(result.successRate) || result.successRate < 0 || result.successRate > 1 ||
    !Number.isInteger(result.trials) || result.trials <= 0 ||
    !Number.isInteger(result.seed)
  ) return state

  return {
    ...state,
    candidates: state.candidates.map((current) => current.id === subject.candidateId
      ? {
          ...current,
          monteCarlo: {
            model: 'Monte Carlo tolerance result',
            candidateId: subject.candidateId,
            inputFingerprint: subject.inputFingerprint,
            successRate: result.successRate,
            trials: result.trials,
            seed: result.seed,
            runAt,
          },
        }
      : current),
  }
}
